import React, { useState, useEffect } from 'react';
import { calculateTimeLeft } from '../../utils/resolutionUtils';
import { useLanguage } from '../../contexts/LanguageContext';

// Animated Flip Card Component
const FlipCard = ({ value, label }) => {
	const [isFlipping, setIsFlipping] = useState(false);
	const [displayValue, setDisplayValue] = useState(value);

	useEffect(() => {
		if (value !== displayValue) {
			setIsFlipping(true);

			// Update value halfway through animation
			setTimeout(() => {
				setDisplayValue(value);
			}, 150);

			// Reset animation
			setTimeout(() => {
				setIsFlipping(false);
			}, 300);
		}
	}, [value, displayValue]);

	return (
		<div className='flip-card-container'>
			<div className={`flip-card-single ${isFlipping ? 'flipping' : ''}`}>
				<div className='bg-gradient-to-b from-gray-700 to-gray-900 text-white rounded-lg border-2 border-gray-600 shadow-lg relative overflow-hidden w-[60px] h-[80px]'>
					<div className='absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-20'></div>
					<div className='flex flex-col justify-center items-center h-full relative z-10'>
						<div className='text-3xl font-bold font-mono leading-none'>
							{String(displayValue).padStart(2, '0')}
						</div>
					</div>
					<div className='absolute bottom-0 left-0 right-0 bg-gray-800 text-xs text-center py-1 text-gray-300'>
						{label.substring(0, 2).toUpperCase()}
					</div>
				</div>
			</div>
		</div>
	);
};

/**
 * Reusable Timer Component
 * @param {number} targetYear - The target year to count down to (e.g., 2025)
 * @param {string} timerType - Timer style: 'basic', 'card', 'clock', 'circular', 'flip', 'icon'
 * @param {string} className - Additional CSS classes
 * @param {boolean} showEndedMessage - Whether to show "Year Ended" message when time is up
 */
const Timer = ({ targetYear, timerType = 'basic', className = '', showEndedMessage = true }) => {
	const [timeComponents, setTimeComponents] = useState({
		months: 0,
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
		ended: false,
	});

	const { t } = useLanguage();

	// Parse time left string into components
	const parseTimeLeft = (timeString) => {
		// Parse time string like "3 months, 7 days, 22 hours, 19 minutes, 19 seconds"
		const match = timeString.match(/(\d+) months?, (\d+) days?, (\d+) hours?, (\d+) minutes?, (\d+) seconds?/);
		if (!match) {
			return { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
		}

		const [, months, days, hours, minutes, seconds] = match;
		return {
			months: parseInt(months, 10),
			days: parseInt(days, 10),
			hours: parseInt(hours, 10),
			minutes: parseInt(minutes, 10),
			seconds: parseInt(seconds, 10),
			ended: false,
		};
	};

	// Update timer every second
	useEffect(() => {
		const updateTimer = () => {
			const timeLeftString = calculateTimeLeft(targetYear);
			const components = parseTimeLeft(timeLeftString);
			setTimeComponents(components);
		};

		// Initial update
		updateTimer();

		// Set up interval for real-time updates
		const interval = setInterval(updateTimer, 1000);

		return () => clearInterval(interval);
	}, [targetYear]);

	// Render timer based on selected type
	const renderTimer = () => {
		switch (timerType) {
			case 'basic':
				// Option 1: Minimalist Single Line
				return (
					<div className='text-center'>
						<div className='text-base sm:text-lg font-mono text-gray-800 whitespace-nowrap overflow-x-auto'>
							<span className='hidden sm:inline'>
								{timeComponents.months} {t('timer.months')} | {timeComponents.days} {t('timer.days')} |{' '}
								{timeComponents.hours} {t('timer.hours')} | {timeComponents.minutes}{' '}
								{t('timer.minutes')} | {timeComponents.seconds} {t('timer.seconds')}
							</span>
							<span className='sm:hidden'>
								{timeComponents.months}
								{t('timer.monthsShort')} | {timeComponents.days}
								{t('timer.daysShort')} | {timeComponents.hours}
								{t('timer.hoursShort')} | {timeComponents.minutes}
								{t('timer.minutesShort')} | {timeComponents.seconds}
								{t('timer.secondsShort')}
							</span>
						</div>
					</div>
				);

			case 'card':
				// Option 2: Colorful Cards - Small colorful badges
				return (
					<div className='flex justify-center space-x-2 sm:space-x-3'>
						{[
							{ value: timeComponents.months, label: t('timer.months'), color: 'bg-purple-500' },
							{ value: timeComponents.days, label: t('timer.days'), color: 'bg-blue-500' },
							{ value: timeComponents.hours, label: t('timer.hours'), color: 'bg-green-500' },
							{ value: timeComponents.minutes, label: t('timer.minutes'), color: 'bg-yellow-500' },
							{ value: timeComponents.seconds, label: t('timer.seconds'), color: 'bg-red-500' },
						].map((item, index) => (
							<div
								key={index}
								className={`${item.color} text-white rounded-lg px-3 py-2 text-center min-w-[4rem]`}
							>
								<div className='text-lg font-bold'>{item.value}</div>
								<div className='text-xs'>{item.label}</div>
							</div>
						))}
					</div>
				);

			case 'clock':
				// Option 3: Digital Clock Style - Terminal-style display
				return (
					<div className='text-center'>
						<div className='bg-black text-green-400 font-mono font-bold px-3 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 rounded-lg inline-block border-2 border-gray-700 shadow-2xl max-w-full overflow-hidden'>
							<div className='text-lg sm:text-3xl md:text-4xl lg:text-5xl'>
								{String(timeComponents.months).padStart(2, '0')}
								<span className='text-xs sm:text-sm text-green-300 font-medium'>
									<span className='sm:hidden'>{t('timer.monthsShortLower')}</span>
									<span className='hidden sm:inline'>{t('timer.monthsShort')}</span>
								</span>
								<span className='text-green-300 mx-1'>:</span>
								{String(timeComponents.days).padStart(2, '0')}
								<span className='text-xs sm:text-sm text-green-300 font-medium'>
									<span className='sm:hidden'>{t('timer.daysShortLower')}</span>
									<span className='hidden sm:inline'>{t('timer.daysShort')}</span>
								</span>
								<span className='text-green-300 mx-1'>:</span>
								{String(timeComponents.hours).padStart(2, '0')}
								<span className='text-xs sm:text-sm text-green-300 font-medium'>
									<span className='sm:hidden'>{t('timer.hoursShortLower')}</span>
									<span className='hidden sm:inline'>{t('timer.hoursShort')}</span>
								</span>
								<span className='text-green-300 mx-1'>:</span>
								{String(timeComponents.minutes).padStart(2, '0')}
								<span className='text-xs sm:text-sm text-green-300 font-medium'>
									<span className='sm:hidden'>{t('timer.minutesShortLower')}</span>
									<span className='hidden sm:inline'>{t('timer.minutesShort')}</span>
								</span>
								<span className='text-green-300 mx-1'>:</span>
								{String(timeComponents.seconds).padStart(2, '0')}
								<span className='text-xs sm:text-sm text-green-300 font-medium'>
									<span className='sm:hidden'>{t('timer.secondsShortLower')}</span>
									<span className='hidden sm:inline'>{t('timer.secondsShort')}</span>
								</span>
							</div>
						</div>
					</div>
				);

			case 'circular':
				// Option 4: Progress Ring/Circle - Modern circular indicators
				return (
					<div className='flex justify-center space-x-4'>
						{[
							{
								value: timeComponents.months,
								max: 12,
								label: t('timer.months'),
								color: 'stroke-purple-500',
							},
							{ value: timeComponents.days, max: 31, label: t('timer.days'), color: 'stroke-blue-500' },
							{
								value: timeComponents.hours,
								max: 24,
								label: t('timer.hours'),
								color: 'stroke-green-500',
							},
							{
								value: timeComponents.minutes,
								max: 60,
								label: t('timer.minutes'),
								color: 'stroke-yellow-500',
							},
							{
								value: timeComponents.seconds,
								max: 60,
								label: t('timer.seconds'),
								color: 'stroke-red-500',
							},
						].map((item, index) => {
							const percentage = (item.value / item.max) * 100;
							const circumference = 2 * Math.PI * 20; // radius = 20
							const strokeDashoffset = circumference - (percentage / 100) * circumference;

							return (
								<div key={index} className='flex flex-col items-center'>
									<div className='relative w-16 h-16 mb-2'>
										<svg className='w-16 h-16 -rotate-90' viewBox='0 0 48 48'>
											<circle
												cx='24'
												cy='24'
												r='20'
												stroke='currentColor'
												strokeWidth='4'
												fill='none'
												className='text-gray-200'
											/>
											<circle
												cx='24'
												cy='24'
												r='20'
												strokeWidth='4'
												fill='none'
												strokeDasharray={circumference}
												strokeDashoffset={strokeDashoffset}
												strokeLinecap='round'
												className={`${item.color} transition-all duration-300`}
											/>
										</svg>
										<div className='absolute inset-0 flex items-center justify-center'>
											<span className='text-sm font-bold'>{item.value}</span>
										</div>
									</div>
									<span className='text-xs text-center'>{item.label}</span>
								</div>
							);
						})}
					</div>
				);

			case 'flip':
				// Option 5: Animated Flip Cards - Vertical flip animation
				return (
					<div className='flex justify-center items-center space-x-3'>
						{[
							{ value: timeComponents.months, label: t('timer.months') },
							{ value: timeComponents.days, label: t('timer.days') },
							{ value: timeComponents.hours, label: t('timer.hours') },
							{ value: timeComponents.minutes, label: t('timer.minutes') },
							{ value: timeComponents.seconds, label: t('timer.seconds') },
						].map((item, index) => (
							<FlipCard key={index} value={item.value} label={item.label} />
						))}
					</div>
				);

			case 'icon':
				// Option 6: Text-Based with Icons
				return (
					<div className='flex justify-center items-center space-x-2 sm:space-x-4 text-sm sm:text-lg whitespace-nowrap overflow-x-auto'>
						<span className='flex items-center space-x-1'>
							<span>📅</span>
							<span className='font-medium'>{timeComponents.months}</span>
							<span className='text-xs sm:text-sm text-gray-600'>{t('timer.monthsShort')}</span>
						</span>
						<span className='flex items-center space-x-1'>
							<span>🗓️</span>
							<span className='font-medium'>{timeComponents.days}</span>
							<span className='text-xs sm:text-sm text-gray-600'>{t('timer.daysShort')}</span>
						</span>
						<span className='flex items-center space-x-1'>
							<span>⏰</span>
							<span className='font-medium'>{timeComponents.hours}</span>
							<span className='text-xs sm:text-sm text-gray-600'>{t('timer.hoursShort')}</span>
						</span>
						<span className='flex items-center space-x-1'>
							<span>⏱️</span>
							<span className='font-medium'>{timeComponents.minutes}</span>
							<span className='text-xs sm:text-sm text-gray-600'>{t('timer.minutesShort')}</span>
						</span>
						<span className='flex items-center space-x-1'>
							<span>⚡</span>
							<span className='font-medium'>{timeComponents.seconds}</span>
							<span className='text-xs sm:text-sm text-gray-600'>{t('timer.secondsShort')}</span>
						</span>
					</div>
				);

			default:
				// Fallback to basic timer for unknown values
				return (
					<div className='text-center'>
						<div className='text-base sm:text-lg font-mono text-gray-800 whitespace-nowrap overflow-x-auto'>
							<span className='hidden sm:inline'>
								{timeComponents.months} Months | {timeComponents.days} Days | {timeComponents.hours}{' '}
								Hours | {timeComponents.minutes} Minutes | {timeComponents.seconds} Seconds
							</span>
							<span className='sm:hidden'>
								{timeComponents.months}M | {timeComponents.days}D | {timeComponents.hours}H |{' '}
								{timeComponents.minutes}m | {timeComponents.seconds}s
							</span>
						</div>
					</div>
				);
		}
	};

	if (timeComponents.ended && showEndedMessage) {
		return (
			<div className={`bg-red-100 border border-red-300 rounded-lg p-4 ${className}`}>
				<p className='text-red-600 font-semibold text-lg text-center'>{t('timer.yearEnded')}</p>
			</div>
		);
	}

	return (
		<div className={className}>
			{renderTimer()}

			{/* FlipCard CSS - only include if using flip cards */}
			{timerType === 'flip' && (
				<style jsx>{`
					.flip-card-container {
						display: inline-block;
						flex-shrink: 0;
					}

					.flip-card-single {
						transition: transform 0.3s ease-in-out;
						transform-origin: center center;
					}

					.flip-card-single.flipping {
						animation: flipVertical 0.3s ease-in-out;
					}

					@keyframes flipVertical {
						0% {
							transform: rotateX(0deg);
						}
						{[
							{ value: timeComponents.months, label: t('timer.months'), color: 'bg-purple-500' },
							{ value: timeComponents.days, label: t('timer.days'), color: 'bg-blue-500' },
							{ value: timeComponents.hours, label: t('timer.hours'), color: 'bg-green-500' },
							{ value: timeComponents.minutes, label: t('timer.minutes'), color: 'bg-yellow-500' },
							{ value: timeComponents.seconds, label: t('timer.seconds'), color: 'bg-red-500' },
						].map((item, index) => (
				`}</style>
			)}
		</div>
	);
};

export default Timer;
